import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Plus, Search, ArrowUp, X } from 'lucide-react';
import LoadingSpinner from '../../ui/LoadingSpinner';
import {
  getInventoryItems,
  getWarehouseTransactions,
  createOutputTransaction,
  getJobs
} from '../../../redux/api';
import { getBusinessType } from '../../../utils/businessTypeUtils';
import { su } from '../../inventory/stockUi';
import NumberField, { parseNumber } from '../../inventory/NumberField';

const InventoryOutputsView = ({ stockKind = 'WAREHOUSE' }) => {
  const { t } = useTranslation();
  const isAsset = stockKind === 'ASSET';
  const [transactions, setTransactions] = useState([]);
  const [items, setItems] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ startDate: '', endDate: '' });
  const [formData, setFormData] = useState({
    itemId: '',
    quantity: '',
    documentNumber: '',
    documentDate: new Date().toISOString().split('T')[0],
    jobId: '',
    reason: isAsset ? 'Izdavanje sredstva' : 'Izdavanje iz magacina',
    partnerName: '',
    notes: '',
    stockKind
  });

  useEffect(() => {
    fetchData();
  }, [dateFilter, stockKind]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const businessType = getBusinessType();
      const [itemsRes, transactionsRes, jobsRes] = await Promise.all([
        getInventoryItems({ stockKind }),
        getWarehouseTransactions({ type: 'OUTPUT', stockKind, ...dateFilter }),
        businessType ? getJobs(businessType) : Promise.resolve({ data: [] })
      ]);
      setItems(itemsRes.data || []);
      setTransactions(transactionsRes.data || []);
      setJobs(jobsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri učitavanju podataka');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createOutputTransaction({ ...formData, quantity: parseNumber(formData.quantity), stockKind });
      toast.success(isAsset ? 'Izlaz sredstva je registrovan' : 'Izlaz robe je registrovan');
      setShowForm(false);
      setFormData({
        itemId: '',
        quantity: '',
        documentNumber: '',
        documentDate: new Date().toISOString().split('T')[0],
        jobId: '',
        reason: isAsset ? 'Izdavanje sredstva' : 'Izdavanje iz magacina',
        partnerName: '',
        notes: '',
        stockKind
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri čuvanju izlaza');
    }
  };

  const filteredTransactions = transactions.filter(t =>
    t.itemId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.documentNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.partnerName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedItem = items.find(item => item._id === formData.itemId);
  const availableQuantity = selectedItem?.availableQuantity || 0;

  if (loading) {
    return (
      <div className={`${su.page} flex items-center justify-center`}>
        <LoadingSpinner size="lg" color="white" />
      </div>
    );
  }

  return (
    <div className={su.page}>
      <div className={su.wrap}>
        <div className={`${su.card} ${su.cardPad}`}>
          <div className={su.toolbar}>
            <div className="min-w-0 flex-1">
              <h1 className={su.title}>
                <ArrowUp className={su.icon} />
                {isAsset
                  ? t('inventory.assetOutputs.title', 'Izlaz sredstava')
                  : t('inventory.outputs.title', 'Izlaz robe')}
              </h1>
              <p className={su.subtitle}>
                {isAsset
                  ? t('inventory.assetOutputs.subtitle', 'Izdavanje alata, opreme i sredstava')
                  : t('inventory.outputs.subtitle', 'Registracija izlaza robe iz magacina')}
              </p>
            </div>
            <div className={su.searchWrap}>
              <Search className={su.searchIcon} />
              <input
                type="text"
                placeholder={t('inventory.outputs.search', 'Pretraži...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={su.searchInput}
              />
            </div>
            <input
              type="date"
              title={t('common.startDate', 'Datum od')}
              value={dateFilter.startDate}
              onChange={(e) => setDateFilter((prev) => ({ ...prev, startDate: e.target.value }))}
              className={`${su.input} ${su.date}`}
              style={{ colorScheme: 'dark' }}
            />
            <input
              type="date"
              title={t('common.endDate', 'Datum do')}
              value={dateFilter.endDate}
              onChange={(e) => setDateFilter((prev) => ({ ...prev, endDate: e.target.value }))}
              className={`${su.input} ${su.date}`}
              style={{ colorScheme: 'dark' }}
            />
            <button onClick={() => setShowForm(true)} className={su.btnPrimary}>
              <Plus className="h-3.5 w-3.5" />
              {t('inventory.outputs.addOutput', 'Dodaj izlaz')}
            </button>
          </div>
        </div>

        {showForm && (
          <div className={`${su.card} ${su.cardPad}`}>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-white">
                {t('inventory.outputs.newOutput', 'Novi izlaz robe')}
              </h2>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-2">
              <div className={su.grid}>
                <div>
                  <label className={su.label}>
                    {t('inventory.name', 'Artikal')} *
                  </label>
                  <select
                    name="itemId"
                    value={formData.itemId}
                    onChange={handleInputChange}
                    required
                    className={su.input}
                  >
                    <option value="">{t('common.select', 'Izaberi...')}</option>
                    {items.filter(item => item.availableQuantity > 0).map(item => (
                      <option key={item._id} value={item._id}>
                        {item.itemCode} - {item.name} (Dostupno: {item.availableQuantity} {item.unit})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={su.label}>
                    {t('inventory.quantity', 'Količina')} *
                  </label>
                  <NumberField
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    step="0.01"
                    required
                    max={availableQuantity || undefined}
                  />
                  {selectedItem && (
                    <span className="text-[11px] text-gray-400 mt-0.5 block">
                      {t('inventory.available', 'Dostupno')}: {availableQuantity} {selectedItem.unit}
                    </span>
                  )}
                </div>
                <div>
                  <label className={su.label}>
                    {t('inventory.outputs.documentNumber', 'Broj dokumenta')} *
                  </label>
                  <input
                    type="text"
                    name="documentNumber"
                    value={formData.documentNumber}
                    onChange={handleInputChange}
                    required
                    className={su.input}
                  />
                </div>
                <div>
                  <label className={su.label}>
                    {t('inventory.outputs.documentDate', 'Datum dokumenta')} *
                  </label>
                  <input
                    type="date"
                    name="documentDate"
                    value={formData.documentDate}
                    onChange={handleInputChange}
                    required
                    className={su.input}
                    style={{ colorScheme: 'dark' }}
                  />
                </div>
                <div>
                  <label className={su.label}>
                    {t('jobs.job', 'Posao')}
                  </label>
                  <select
                    name="jobId"
                    value={formData.jobId}
                    onChange={handleInputChange}
                    className={su.input}
                  >
                    <option value="">{t('common.select', 'Izaberi posao (opciono)...')}</option>
                    {jobs.map(job => (
                      <option key={job._id} value={job._id}>
                        {job.clientName} - {job.issueDescription?.substring(0, 50)}... ({new Date(job.serviceDate || job.createdAt).toLocaleDateString('sr-RS')})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={su.label}>
                    {t('inventory.outputs.reason', 'Razlog')}
                  </label>
                  <input
                    type="text"
                    name="reason"
                    value={formData.reason}
                    onChange={handleInputChange}
                    className={su.input}
                  />
                </div>
              </div>
              <div>
                <label className={su.label}>
                  {t('common.notes', 'Napomene')}
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="2"
                  className={su.textarea}
                />
              </div>
              <div className="flex justify-end gap-1.5">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className={su.btnGhost}
                >
                  {t('common.cancel', 'Otkaži')}
                </button>
                <button type="submit" className={su.btnPrimary}>
                  {t('common.save', 'Sačuvaj')}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className={`${su.card} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={su.tableHead}>
                <tr>
                  <th className={su.th}>{t('inventory.outputs.documentNumber', 'Dokument')}</th>
                  <th className={su.th}>{t('inventory.outputs.documentDate', 'Datum')}</th>
                  <th className={su.th}>{t('inventory.name', 'Artikal')}</th>
                  <th className={su.th}>{t('inventory.quantity', 'Kol.')}</th>
                  <th className={su.th}>{t('inventory.outputs.reason', 'Razlog')}</th>
                  <th className={su.th}>{t('jobs.job', 'Posao')}</th>
                  <th className={su.th}>{t('inventory.outputs.customer', 'Kupac')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/70">
                {filteredTransactions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className={su.empty}>
                      {t('inventory.outputs.noOutputs', 'Nema izlaza robe')}
                    </td>
                  </tr>
                ) : (
                  filteredTransactions.map((transaction) => (
                    <tr key={transaction._id} className={su.tableRow}>
                      <td className={su.td}>{transaction.documentNumber}</td>
                      <td className={su.td}>
                        {new Date(transaction.documentDate).toLocaleDateString('sr-RS')}
                      </td>
                      <td className={su.td}>{transaction.itemId?.name || '-'}</td>
                      <td className={su.td}>
                        {transaction.quantity} {transaction.unit}
                      </td>
                      <td className={`${su.td} text-gray-400`}>{transaction.reason || '-'}</td>
                      <td className={su.td}>{transaction.jobId?.clientName || '-'}</td>
                      <td className={`${su.td} text-gray-400`}>{transaction.partnerName || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryOutputsView;

